import React, { useState } from 'react';
import Typography from "@mui/material/Typography";
import { Automation } from "../../api/AutomationsApi";
import { Chip } from "@mui/material";
import {BaseTable, Column} from "../../components/BaseTable";

interface AutomationsTableProps {
    automations: Automation[];
}

export const AutomationsTable: React.FC<AutomationsTableProps> = ({ automations }) => {
    const [orderBy, setOrderBy] = useState<string>('name');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');

    const handleSortWrapper = (property: string, newOrder: 'asc' | 'desc') => {
        setOrderBy(property);
        setOrder(newOrder);
    };

    const columns: Column<Automation>[] = [
        {
            key: 'name',
            label: 'Name',
            render: (value, row) => (
                <Typography variant="body2">
                    {row.name || row.id}
                </Typography>
            )
        },
        {
            key: 'description',
            label: 'Beschreibung',
            render: (value) => (
                <Typography variant="body2" sx={{ maxWidth: 800 }}>
                    {value || '-'}
                </Typography>
            )
        },
        {
            key: 'conditions',
            label: 'Bedingungen',
            align: 'center' as const,
            render: (value, row) => (
                <Chip
                    label={row.conditions?.length || 0}
                    size="small"
                    variant="outlined"
                    color="primary"
                />
            )
        },
        {
            key: 'actions',
            label: 'Aktionen',
            align: 'center' as const,
            render: (value, row) => (
                <Chip
                    label={row.actions?.length || 0}
                    size="small"
                    variant="outlined"
                    color="secondary"
                />
            )
        },
        {
            key: 'status',
            label: 'Status',
            align: 'center' as const,
            render: (value, row) => {
                const isComplete = row.enabled;

                return (
                    <Chip
                        label={isComplete ? 'Aktiv' : 'Inaktiv'}
                        size="small"
                        color={isComplete ? 'success' : 'warning'}
                        variant="filled"
                    />
                );
            }
        }
    ];

    // Sort the automations data based on current sort settings
    const sortedAutomations = React.useMemo(() => {
        return [...automations].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (orderBy) {
                case 'name':
                    aValue = a.name || a.id;
                    bValue = b.name || b.id;
                    break;
                case 'id':
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case 'description':
                    aValue = a.description || '';
                    bValue = b.description || '';
                    break;
                case 'conditions':
                    aValue = a.conditions?.length || 0;
                    bValue = b.conditions?.length || 0;
                    break;
                case 'actions':
                    aValue = a.actions?.length || 0;
                    bValue = b.actions?.length || 0;
                    break;
                default:
                    aValue = a.name || a.id;
                    bValue = b.name || b.id;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.localeCompare(bValue);
                return order === 'asc' ? comparison : -comparison;
            } else {
                const comparison = aValue - bValue;
                return order === 'asc' ? comparison : -comparison;
            }
        });
    }, [automations, orderBy, order]);

    return (
        <BaseTable
            data={sortedAutomations}
            columns={columns}
            orderBy={orderBy}
            order={order}
            onSort={handleSortWrapper}
            rowKey={(row) => row.id}
            showIndex={true}
        />
    );
};

export default AutomationsTable;